// Notification Service for managing deposit notifications

export interface Notification {
  id: string;
  type: 'deposit' | 'withdrawal' | 'game' | 'info';
  title: string;
  message: string;
  amount?: number;
  timestamp: Date;
  read: boolean;
}

class NotificationService {
  private static instance: NotificationService;
  private notifications: Notification[] = [];
  private subscribers: ((notifications: Notification[]) => void)[] = [];
  private nextId = 1;

  private constructor() {}

  static getInstance(): NotificationService {
    if (!NotificationService.instance) {
      NotificationService.instance = new NotificationService();
    }
    return NotificationService.instance;
  }

  subscribe(callback: (notifications: Notification[]) => void): () => void {
    this.subscribers.push(callback);
    // Send current notifications immediately
    callback([...this.notifications]);
    return () => {
      this.subscribers = this.subscribers.filter(sub => sub !== callback);
    };
  }

  private notify(): void {
    this.subscribers.forEach(callback => callback([...this.notifications]));
  }

  addNotification(notification: Omit<Notification, 'id' | 'timestamp' | 'read'>): void {
    const newNotification: Notification = {
      ...notification,
      id: `notif-${this.nextId++}-${Date.now()}`,
      timestamp: new Date(),
      read: false
    };

    // Add to beginning of array (newest first)
    this.notifications.unshift(newNotification);
    
    // Keep only last 50 notifications
    if (this.notifications.length > 50) {
      this.notifications = this.notifications.slice(0, 50);
    }

    this.notify();
  }

  markAsRead(id: string): void {
    const notification = this.notifications.find(n => n.id === id);
    if (notification && !notification.read) {
      notification.read = true;
      this.notify();
    }
  }

  markAllAsRead(): void {
    let changed = false;
    this.notifications.forEach(n => {
      if (!n.read) {
        n.read = true;
        changed = true;
      }
    });
    if (changed) {
      this.notify();
    }
  }

  getUnreadCount(): number {
    return this.notifications.filter(n => !n.read).length;
  }

  getNotifications(): Notification[] {
    return [...this.notifications];
  }

  clearAll(): void {
    this.notifications = [];
    this.notify();
  }

  removeNotification(id: string): void {
    this.notifications = this.notifications.filter(n => n.id !== id);
    this.notify();
  }

  loadNotifications(notifications: Notification[]): void {
    this.notifications = notifications.sort((a, b) => 
      b.timestamp.getTime() - a.timestamp.getTime()
    );
    this.notify();
  }
}

export const notificationService = NotificationService.getInstance();
